import { apiClient } from '../utils/api-client';
import { downloadBlobAsFile } from './documentService';

import type {
  FundInvestmentRows,
  PortfolioCompany,
  PortfolioCompanyListParams,
  PortfolioCompanyPage,
  PortfolioCompanyDetail,
  PortfolioSummary,
  FundValuationsSummary,
  PortfolioValuation,
  PortfolioFinancialMetrics,
  PortfolioDocument,
  CreatePortfolioCompanyRequest,
  UpdatePortfolioCompanyRequest,
  CreateValuationRequest,
  CreateFinancialMetricsRequest,
  FinancialMetricsImportMode,
  FinancialMetricsImportResult,
  CreateDocumentRequest,
  PortfolioCompanyBankAccount,
  BankAccountWriteRequest,
  PortfolioContact,
  CreateContactRequest,
  UpdateContactRequest,
  DeletionEligibility,
} from '../types/portfolio';
import type { CapTableFinality } from '../types/captable';

export const portfolioService = {
  // Portfolio Summary
  async getPortfolioSummary(
    fundId?: string,
    fundInvestments?: FundInvestmentRows
  ): Promise<PortfolioSummary> {
    return apiClient.get<PortfolioSummary>('/portfolio/summary', {
      params: { fund_id: fundId, fund_investments: fundInvestments },
    });
  },

  // Fund Valuations Summary
  async getFundValuationsSummary(
    fundId: string
  ): Promise<FundValuationsSummary> {
    return apiClient.get<FundValuationsSummary>(
      '/portfolio/valuations/fund-summary',
      { params: { fund_id: fundId } }
    );
  },

  // Portfolio Companies
  async getPortfolioCompanies(
    params?: PortfolioCompanyListParams
  ): Promise<PortfolioCompanyPage> {
    return apiClient.get<PortfolioCompanyPage>('/portfolio/companies', {
      params,
    });
  },

  async getPortfolioSectors(
    fundId?: string,
    fundInvestments?: FundInvestmentRows
  ): Promise<string[]> {
    return apiClient.get<string[]>('/portfolio/companies/sectors', {
      params: { fund_id: fundId, fund_investments: fundInvestments },
    });
  },

  async getPortfolioCompany(
    companyId: string,
    finality?: CapTableFinality
  ): Promise<PortfolioCompanyDetail> {
    return apiClient.get<PortfolioCompanyDetail>(
      `/portfolio/companies/${companyId}`,
      { params: { finality } }
    );
  },

  async createPortfolioCompany(
    data: CreatePortfolioCompanyRequest
  ): Promise<PortfolioCompany> {
    return apiClient.post<PortfolioCompany>('/portfolio/companies', data);
  },

  async updatePortfolioCompany(
    companyId: string,
    data: UpdatePortfolioCompanyRequest
  ): Promise<PortfolioCompany> {
    return apiClient.put<PortfolioCompany>(
      `/portfolio/companies/${companyId}`,
      data
    );
  },

  async deletePortfolioCompany(companyId: string): Promise<void> {
    return apiClient.delete(`/portfolio/companies/${companyId}`);
  },

  async getDeletionEligibility(
    companyId: string
  ): Promise<DeletionEligibility> {
    return apiClient.get<DeletionEligibility>(
      `/portfolio/companies/${companyId}/deletion-eligibility`
    );
  },

  async archivePortfolioCompany(companyId: string): Promise<{ id: string }> {
    return apiClient.post<{ id: string }>(
      `/portfolio/companies/${companyId}/archive`,
      {}
    );
  },

  // Valuations
  async getValuations(companyId: string): Promise<PortfolioValuation[]> {
    return apiClient.get<PortfolioValuation[]>(
      `/portfolio/companies/${companyId}/valuations`
    );
  },

  async createValuation(
    companyId: string,
    data: CreateValuationRequest
  ): Promise<PortfolioValuation> {
    return apiClient.post<PortfolioValuation>(
      `/portfolio/companies/${companyId}/valuations`,
      data
    );
  },

  async approveValuation(
    companyId: string,
    valuationId: string
  ): Promise<PortfolioValuation> {
    return apiClient.post<PortfolioValuation>(
      `/portfolio/companies/${companyId}/valuations/${valuationId}/approve`
    );
  },

  // Financial Metrics
  async getFinancialMetrics(
    companyId: string,
    periodType?: string
  ): Promise<PortfolioFinancialMetrics[]> {
    return apiClient.get<PortfolioFinancialMetrics[]>(
      `/portfolio/companies/${companyId}/metrics`,
      { params: { period_type: periodType } }
    );
  },

  async getFinancialMetricsPaginated(
    companyId: string,
    params?: {
      period_type?: string;
      sort_by?: string;
      sort_order?: string;
      limit?: number;
      offset?: number;
      date_from?: string;
      date_to?: string;
    }
  ): Promise<{
    items: PortfolioFinancialMetrics[];
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  }> {
    return apiClient.get(`/portfolio/companies/${companyId}/metrics`, {
      params,
    });
  },

  async createFinancialMetrics(
    companyId: string,
    data: CreateFinancialMetricsRequest
  ): Promise<PortfolioFinancialMetrics> {
    return apiClient.post<PortfolioFinancialMetrics>(
      `/portfolio/companies/${companyId}/metrics`,
      data
    );
  },

  async updateFinancialMetrics(
    companyId: string,
    metricsId: string,
    data: CreateFinancialMetricsRequest
  ): Promise<PortfolioFinancialMetrics> {
    return apiClient.put<PortfolioFinancialMetrics>(
      `/portfolio/companies/${companyId}/metrics/${metricsId}`,
      data
    );
  },

  async importFinancialMetrics(
    companyId: string,
    file: File,
    mode: FinancialMetricsImportMode
  ): Promise<FinancialMetricsImportResult> {
    const body = new FormData();
    body.append('file', file);
    body.append('mode', mode);
    return apiClient.post<FinancialMetricsImportResult>(
      `/portfolio/companies/${companyId}/metrics/import`,
      body
    );
  },

  async downloadFinancialMetricsTemplate(companyId: string): Promise<void> {
    const blob = await apiClient.getBlob(
      `/portfolio/companies/${companyId}/metrics/import/template`
    );
    downloadBlobAsFile(blob, 'financial-metrics-template.xlsx');
  },

  // Documents
  async getDocuments(
    companyId: string,
    documentType?: string
  ): Promise<PortfolioDocument[]> {
    return apiClient.get<PortfolioDocument[]>(
      `/portfolio/companies/${companyId}/documents`,
      { params: { document_type: documentType } }
    );
  },

  async createDocument(
    companyId: string,
    data: CreateDocumentRequest
  ): Promise<PortfolioDocument> {
    return apiClient.post<PortfolioDocument>(
      `/portfolio/companies/${companyId}/documents`,
      data
    );
  },

  // Company Formation
  async createCompanyFormation(
    companyId: string,
    data: {
      formation_date: string;
      founder_shares: Array<{
        name: string;
        shares: number;
        stakeholder_type?: string;
      }>;
      share_price: number;
      currency_id: string;
      include_option_pool: boolean;
      option_pool_size?: number;
    }
  ): Promise<unknown> {
    return apiClient.post<unknown>(
      `/formation/companies/${companyId}/formation`,
      data
    );
  },

  async checkFormationStatus(companyId: string): Promise<{
    company_id: string;
    has_formation: boolean;
    transaction_count: number;
  }> {
    return apiClient.get<{
      company_id: string;
      has_formation: boolean;
      transaction_count: number;
    }>(`/formation/companies/${companyId}/formation/status`);
  },

  // Portfolio Company Contacts
  async getContacts(companyId: string): Promise<PortfolioContact[]> {
    return apiClient.get<PortfolioContact[]>(
      `/portfolio/companies/${companyId}/contacts`
    );
  },

  async createContact(
    companyId: string,
    contactData: CreateContactRequest
  ): Promise<PortfolioContact> {
    return apiClient.post<PortfolioContact>(
      `/portfolio/companies/${companyId}/contacts`,
      contactData
    );
  },

  async updateContact(
    companyId: string,
    contactId: string,
    updates: UpdateContactRequest
  ): Promise<PortfolioContact> {
    return apiClient.put<PortfolioContact>(
      `/portfolio/companies/${companyId}/contacts/${contactId}`,
      updates
    );
  },

  async deleteContact(companyId: string, contactId: string): Promise<void> {
    return apiClient.delete(
      `/portfolio/companies/${companyId}/contacts/${contactId}`
    );
  },

  // Portfolio Company Bank Accounts
  async getBankAccounts(
    companyId: string
  ): Promise<PortfolioCompanyBankAccount[]> {
    return apiClient.get<PortfolioCompanyBankAccount[]>(
      `/portfolio/companies/${companyId}/bank-accounts`
    );
  },

  // The whole set in one request: the server closes the accounts it leaves out,
  // so a save that fails leaves nothing behind to be sent again as a duplicate.
  async syncBankAccounts(
    companyId: string,
    bankAccounts: BankAccountWriteRequest[]
  ): Promise<PortfolioCompanyBankAccount[]> {
    return apiClient.put<PortfolioCompanyBankAccount[]>(
      `/portfolio/companies/${companyId}/bank-accounts`,
      bankAccounts
    );
  },
};
